import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Admin from './pages/Admin/Admin';
import Header from './components/Header/Header';
import BottomNav from './components/BottomNav/BottomNav';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Home from './pages/Home/Home';
import PostPage from './pages/Home/PostPage';
import Family from './pages/Family/Family';
import Profile from './pages/Profile/Profile';
import MemberProfile from './pages/Profile/MemberProfile';
import Photos from './pages/Photos/Photos';
import AlbumView from './pages/Photos/AlbumView';
import Recipes from './pages/Recipes/Recipes';
import Events from './pages/Events/Events';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import { UIProvider, useUI } from './context/UIContext';
import CreatePost from './components/CreatePost/CreatePost';
import { useNavigate } from 'react-router-dom';
import EventAlbums from './pages/Photos/EventAlbums';
import Notifications from './pages/Notifications/Notifications';
import Settings from './pages/Settings/Settings';
import Stories from './pages/Stories/Stories';
import FamilyAlbums from './pages/Photos/FamilyAlbums';

import './styles/global.css';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-overlay">
        <span className="spinner spinner-lg"></span>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to home if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-overlay">
        <span className="spinner spinner-lg"></span>
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Placeholder components for pages we'll build later
const PlaceholderPage = ({ title }) => (
  <div className="container" style={{ paddingTop: '2rem' }}>
    <div className="card">
      <div className="card-body" style={{ textAlign: 'center', padding: '3rem' }}>
        <h2>{title}</h2>
        <p className="text-muted">This page is coming soon!</p>
      </div>
    </div>
  </div>
);

const Messages = () => <PlaceholderPage title="Messages" />;

function AppContent() {
  const { isAuthenticated } = useAuth();
  const { user } = useAuth();
  const { showCreatePost, closeCreatePost } = useUI();
  const navigate = useNavigate();

  const handlePostCreated = (newPost) => {
    closeCreatePost();
    navigate('/');
  };

  return (
    <div className="app">
      <Header />

      {/* Global Create Post Modal */}
      {showCreatePost && (
        <div className="modal-overlay" onClick={closeCreatePost}>
          <div className="create-post-modal" onClick={e => e.stopPropagation()}>
            <CreatePost
              user={user}
              onPostCreated={handlePostCreated}
              onClose={closeCreatePost}
            />
          </div>
        </div>
      )}

      <main className="main-content">
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/post/:id"
            element={
              <ProtectedRoute>
                <PostPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/family"
            element={
              <ProtectedRoute>
                <Family />
              </ProtectedRoute>
            }
          />
          <Route
            path="/photos"
            element={
              <ProtectedRoute>
                <Photos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/photos/album/:id"
            element={
              <ProtectedRoute>
                <AlbumView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/event-albums"
            element={
              <ProtectedRoute>
                <EventAlbums />
              </ProtectedRoute>
            }
          />
          <Route
            path="/family-albums"
            element={
              <ProtectedRoute>
                <FamilyAlbums />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recipes"
            element={
              <ProtectedRoute>
                <Recipes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create"
            element={<Navigate to="/" replace />}
          />
          <Route
            path="/member/:id"
            element={
              <ProtectedRoute>
                <MemberProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />

          <Route
            path="/stories"
            element={
              <ProtectedRoute>
                <Stories />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Bottom Nav - only show when authenticated */}
      {isAuthenticated && <BottomNav />}

      {/* Toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <UIProvider>
          <AppContent />
        </UIProvider>
      </AuthProvider>
    </Router>
  );
}

// ============================================
// RECIPE ENDPOINTS
// ============================================
export const recipeService = {
  getAllRecipes: async (category = null, search = null) => {
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    const response = await api.get('/recipes', { params });
    return response.data;
  },

  getRecipe: async (id) => {
    const response = await api.get(`/recipes/${id}`);
    return response.data;
  },

  getCategories: async () => {
    const response = await api.get('/recipes/categories');
    return response.data;
  },

  createRecipe: async (data) => {
    const response = await api.post('/recipes', data);
    return response.data;
  },

  updateRecipe: async (id, data) => {
    const response = await api.put(`/recipes/${id}`, data);
    return response.data;
  },

  deleteRecipe: async (id) => {
    const response = await api.delete(`/recipes/${id}`);
    return response.data;
  },
};

export default App;
